import { Injectable, inject } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest, PopupRequest, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { Observable, BehaviorSubject, filter } from 'rxjs';
import { User } from '@core/models/user.model';
import { environment } from '@environments/environment';

/**
 * Authentication service wrapper for MSAL
 * Provides simplified authentication methods and user state management
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor() {
    // MSAL is already initialized via APP_INITIALIZER
    console.log('🔐 AuthService initializing (MSAL already ready)...');
    console.log('Azure AD Config:', {
      clientId: environment.azureAd.clientId,
      authority: environment.azureAd.authority,
      redirectUri: environment.azureAd.redirectUri
    });
    
    // Set up auth state immediately since MSAL is ready
    this.initializeUserState();
  }

  /**
   * Initialize user state from MSAL account
   */
  private initializeUserState(): void {
    try {
      // Immediately set active account from existing accounts
      this.setActiveAccount();
      
      // Also listen for MSAL interaction changes
      this.msalBroadcastService.inProgress$
        .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
        .subscribe(() => {
          console.log('🔐 MSAL interaction complete, refreshing active account...');
          this.setActiveAccount();
        });
    } catch (error) {
      console.error('❌ Failed to initialize MSAL auth state:', error);
    }
  }

  /**
   * Set the active account and update current user
   */
  private setActiveAccount(): void {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      console.log(`🔐 Found ${accounts.length} MSAL accounts`);
    
      if (accounts.length > 0) {
        const activeAccount = accounts[0];
        console.log('🔐 Active account:', {
          name: activeAccount.name,
          username: activeAccount.username,
          localAccountId: activeAccount.localAccountId
        });
        this.msalService.instance.setActiveAccount(activeAccount);
        this.currentUserSubject.next(this.mapAccountToUser(activeAccount));
      } else {
        console.log('⚠️ No MSAL accounts found - user needs to login');
        this.currentUserSubject.next(null);
      }
    } catch (error) {
      console.error('❌ Error setting active account:', error);
      this.currentUserSubject.next(null);
    }
  }

  /**
   * Map MSAL account info to User model
   * Extracts security groups from token claims
   */
  private mapAccountToUser(account: AccountInfo): User {
    // Get security groups from token claims
    const groups = (account.idTokenClaims?.['groups'] as string[]) || [];
    
    console.log('👤 Mapping account to user:', {
      accountId: account.localAccountId,
      name: account.name,
      username: account.username,
      groupsFound: groups.length,
      groups: groups
    });
    
    // Map Azure AD groups to application roles
    const roles: string[] = [];
    const azureAdConfig = environment.azureAd as any;
    const adminGroupId = azureAdConfig.groups?.admins;
    const userGroupId = azureAdConfig.groups?.users;
    
    if (adminGroupId && groups.includes(adminGroupId)) {
      roles.push('admin');
      console.log('✅ User has ADMIN role');
    }
    if (userGroupId && groups.includes(userGroupId)) {
      roles.push('user');
      console.log('✅ User has USER role');
    }
    
    if (roles.length === 0) {
      console.warn('⚠️ User has NO roles - not assigned to any security groups!');
      console.warn('Expected groups:', {
        admins: adminGroupId,
        users: userGroupId
      });
    }
    
    return {
      id: account.localAccountId,
      name: account.name || account.username,
      email: account.username,
      username: account.username,
      roles: roles,
      groups: groups // Store raw group IDs for advanced scenarios
    };
  }

  /**
   * Initiate login flow (redirect)
   */
  login(): void {
    console.log('🔐 Initiating login redirect to Azure AD...');
    const loginRequest: RedirectRequest = {
      scopes: ['openid', 'profile', 'email']
    };
    
    try {
      this.msalService.loginRedirect(loginRequest);
    } catch (error) {
      console.error('❌ Login redirect failed:', error);
    }
  }

  /**
   * Initiate login flow (popup)
   */
  loginPopup(): Observable<AuthenticationResult> {
    const loginRequest: PopupRequest = {
      scopes: ['openid', 'profile', 'email']
    };
    
    return this.msalService.loginPopup(loginRequest);
  }

  /**
   * Logout the current user
   */
  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: '/'
    });
  }

  /**
   * Get current authenticated user
   */
  getUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      const isAuth = accounts.length > 0;
      console.log(`🔐 isAuthenticated called: ${isAuth} (${accounts.length} accounts)`);
      return isAuth;
    } catch (error) {
      console.error('❌ Error checking authentication:', error);
      return false;
    }
  }

  /**
   * Get the current access token
   * @param scopes Optional scopes to request
   */
  async getAccessToken(scopes?: string[]): Promise<string | null> {
    const activeAccount = this.msalService.instance.getActiveAccount();
    
    if (!activeAccount) {
      return null;
    }

    const tokenRequest = {
      scopes: scopes || ['openid', 'profile'],
      account: activeAccount
    };

    try {
      const response = await this.msalService.instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      console.error('Token acquisition failed:', error);
      // If silent token acquisition fails, could trigger interactive flow
      // For now, return null
      return null;
    }
  }

  /**
   * Get user's display name
   */
  getUserDisplayName(): string {
    const user = this.getUser();
    return user?.name || user?.email || 'User';
  }

  /**
   * Get user's email
   */
  getUserEmail(): string {
    const user = this.getUser();
    return user?.email || '';
  }

  /**
   * Check if user has a specific role (mapped from security groups)
   */
  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.roles?.includes(role) || false;
  }

  /**
   * Check if user is a member of a specific Azure AD group
   * @param groupId Azure AD group Object ID
   */
  hasGroup(groupId: string): boolean {
    const user = this.getUser();
    return user?.groups?.includes(groupId) || false;
  }

  /**
   * Check if user is an admin (member of admin security group)
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Get all user's security group IDs
   */
  getUserGroups(): string[] {
    const user = this.getUser();
    return user?.groups || [];
  }
}
